import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Percent, X, History, Search, Tag, Trash2, CheckSquare, Square, Zap } from "lucide-react";
import { toast } from "sonner";
import { API } from "./SharedComponents";

const SubscribersManager = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [discountModal, setDiscountModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [discountHistory, setDiscountHistory] = useState([]);
  const [form, setForm] = useState({ percent_off: "", note: "" });
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({ percent_off: "", note: "" });
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => { fetchSubscribers(); }, []);

  const fetchSubscribers = async () => {
    try {
      const res = await fetch(`${API}/admin/subscribers`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSubscribers(data.subscribers || []);
      }
    } catch { toast.error("Failed to load subscribers"); }
    finally { setLoading(false); }
  };

  const handleSetDiscount = async () => {
    const pct = parseFloat(form.percent_off);
    if (!pct || pct <= 0 || pct > 100) return toast.error("Enter a valid percentage (1-100)");
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/subscribers/${discountModal.user_id}/discount`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ percent_off: pct, note: form.note }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message + (data.stripe_applied ? " (Stripe updated)" : " (saved locally)"));
        setDiscountModal(null);
        setForm({ percent_off: "", note: "" });
        fetchSubscribers();
      } else toast.error("Failed to set discount");
    } catch { toast.error("Something went wrong"); }
    finally { setSaving(false); }
  };

  const handleRemoveDiscount = async (userId) => {
    if (!window.confirm("Remove this athlete's discount?")) return;
    try {
      const res = await fetch(`${API}/admin/subscribers/${userId}/discount`, {
        method: "DELETE", credentials: "include",
      });
      if (res.ok) { toast.success("Discount removed"); fetchSubscribers(); }
    } catch { toast.error("Failed to remove discount"); }
  };

  const openHistory = async (user) => {
    setHistoryModal(user);
    try {
      const res = await fetch(`${API}/admin/subscribers/${user.user_id}/discount-history`, { credentials: "include" });
      if (res.ok) setDiscountHistory((await res.json()).history || []);
    } catch { setDiscountHistory([]); }
  };

  const openDiscount = (user) => {
    setDiscountModal(user);
    setForm({
      percent_off: user.discount?.percent_off?.toString() || "",
      note: user.discount?.note || "",
    });
  };

  const toggleSelect = (userId) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(s => s.user_id)));
    }
  };

  const handleBulkDiscount = async () => {
    const pct = parseFloat(bulkForm.percent_off);
    if (!pct || pct <= 0 || pct > 100) return toast.error("Enter a valid percentage (1-100)");
    setBulkSaving(true);
    try {
      const res = await fetch(`${API}/admin/subscribers/bulk-discount`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_ids: [...selected], percent_off: pct, note: bulkForm.note }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setBulkModal(false);
        setBulkForm({ percent_off: "", note: "" });
        setSelected(new Set());
        fetchSubscribers();
      } else toast.error("Bulk discount failed");
    } catch { toast.error("Something went wrong"); }
    finally { setBulkSaving(false); }
  };

  const handleBulkRemove = async () => {
    if (!window.confirm(`Remove discount from ${selected.size} athlete(s)?`)) return;
    try {
      const res = await fetch(`${API}/admin/subscribers/bulk-remove-discount`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_ids: [...selected] }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setSelected(new Set());
        fetchSubscribers();
      }
    } catch { toast.error("Failed to remove discounts"); }
  };

  const filtered = subscribers.filter(s =>
    !search ||
    (s.name || "").toLowerCase().includes(search.toLowerCase()) ||
    (s.email || "").toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="subscribers-manager">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white">Subscribers</h2>
          <p className="text-sm text-gray-400 mt-1">{subscribers.length} athlete{subscribers.length !== 1 ? "s" : ""}</p>
        </div>
      </div>

      {subscribers.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full pl-10 pr-4 py-2.5 bg-[#141B2D] border border-[#1E293B] rounded-xl text-white text-sm focus:border-blue-500 outline-none"
            data-testid="subscribers-search"
          />
        </div>
      )}

      {/* Bulk Action Bar */}
      <AnimatePresence>
        {selected.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl"
            data-testid="bulk-action-bar"
          >
            <span className="text-sm font-medium text-blue-400">{selected.size} selected</span>
            <div className="flex-1" />
            <button
              onClick={() => { setBulkForm({ percent_off: "", note: "" }); setBulkModal(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded-lg text-xs font-medium hover:bg-amber-500/30 transition-colors"
              data-testid="bulk-set-discount-btn"
            >
              <Zap className="w-3.5 h-3.5" />
              Bulk Discount
            </button>
            <button
              onClick={handleBulkRemove}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-xs font-medium hover:bg-red-500/30 transition-colors"
              data-testid="bulk-remove-discount-btn"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Remove Discounts
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-[#1E293B] transition-colors"
              data-testid="bulk-clear-selection-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-[#141B2D] border border-[#1E293B] rounded-2xl">
          <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">{search ? "No matching athletes" : "No subscribers yet"}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Select All Row */}
          <div className="flex items-center gap-3 px-5 py-2">
            <button onClick={toggleSelectAll} className="text-gray-500 hover:text-blue-400 transition-colors" data-testid="select-all-btn">
              {selected.size === filtered.length ? <CheckSquare className="w-5 h-5 text-blue-400" /> : <Square className="w-5 h-5" />}
            </button>
            <span className="text-xs text-gray-500 font-medium">
              {selected.size === filtered.length ? "Deselect all" : "Select all"}
            </span>
          </div>

          {filtered.map((sub) => (
            <div key={sub.user_id}
              className={`bg-[#141B2D] border rounded-2xl p-5 transition-all ${
                selected.has(sub.user_id) ? "border-blue-500/40 bg-blue-500/5" : "border-[#1E293B] hover:border-blue-500/20"
              }`}
              data-testid={`subscriber-${sub.user_id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {/* Checkbox */}
                  <button onClick={() => toggleSelect(sub.user_id)} className="text-gray-500 hover:text-blue-400 transition-colors flex-shrink-0" data-testid={`select-${sub.user_id}`}>
                    {selected.has(sub.user_id) ? <CheckSquare className="w-5 h-5 text-blue-400" /> : <Square className="w-5 h-5" />}
                  </button>

                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 font-bold text-sm flex-shrink-0">
                    {(sub.name || sub.email || "?")[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-white text-sm truncate">{sub.name || "Unnamed"}</h3>
                    <p className="text-xs text-gray-500 truncate">{sub.email}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium uppercase tracking-wider ${
                      sub.subscription_status === "active" ? "bg-emerald-500/20 text-emerald-400" :
                      sub.subscription_status === "trialing" ? "bg-blue-500/20 text-blue-400" :
                      "bg-gray-500/20 text-gray-400"
                    }`}>
                      {sub.subscription_status || "inactive"}
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1E293B] text-gray-400 font-medium uppercase tracking-wider">
                      {sub.subscription_tier || "free"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  {sub.discount && (
                    <div className="flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-1.5">
                      <Percent className="w-3.5 h-3.5 text-amber-400" />
                      <span className="text-sm font-bold text-amber-400">{sub.discount.percent_off}% off</span>
                      {sub.discount.note && (
                        <span className="text-[10px] text-amber-400/60 ml-1">({sub.discount.note})</span>
                      )}
                      <button onClick={() => handleRemoveDiscount(sub.user_id)} className="ml-1 p-0.5 hover:bg-red-500/20 rounded text-amber-400/50 hover:text-red-400 transition-colors" data-testid={`remove-discount-${sub.user_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => openDiscount(sub)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all ${
                      sub.discount
                        ? "bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/20"
                        : "bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border border-blue-500/20"
                    }`}
                    data-testid={`set-discount-${sub.user_id}`}
                  >
                    <Tag className="w-3.5 h-3.5" />
                    {sub.discount ? "Change" : "Set Discount"}
                  </button>
                  <button onClick={() => openHistory(sub)} className="p-2 rounded-lg bg-[#1E293B] text-gray-400 hover:text-white hover:bg-[#2D3B4F] transition-colors" data-testid={`discount-history-${sub.user_id}`}>
                    <History className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Set Discount Modal (Single) */}
      <AnimatePresence>
        {discountModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setDiscountModal(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-[#0F172A] border border-[#1E293B] rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="discount-modal">
              <div className="flex justify-between items-center mb-5">
                <div>
                  <h3 className="text-lg font-bold text-white">Set Discount</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{discountModal.name || discountModal.email}</p>
                </div>
                <button onClick={() => setDiscountModal(null)} className="p-1 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Discount Percentage *</label>
                  <div className="relative">
                    <input type="number" min="1" max="100" step="0.01" value={form.percent_off}
                      onChange={(e) => setForm({ ...form, percent_off: e.target.value })}
                      placeholder="e.g., 50"
                      className="w-full px-3 py-2.5 bg-[#0B0F1A] border border-[#1E293B] rounded-xl text-white text-sm focus:border-blue-500 outline-none pr-10"
                      data-testid="discount-percent-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Note / Reason</label>
                  <input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="e.g., Bundle discount, loyalty"
                    className="w-full px-3 py-2.5 bg-[#0B0F1A] border border-[#1E293B] rounded-xl text-white text-sm focus:border-blue-500 outline-none"
                    data-testid="discount-note-input"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setDiscountModal(null)} className="flex-1 px-4 py-2.5 bg-[#1E293B] text-gray-300 rounded-xl font-medium hover:bg-[#2D3B4F] transition-colors">Cancel</button>
                  <button onClick={handleSetDiscount} disabled={saving}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-medium hover:from-blue-500 hover:to-blue-400 transition-all disabled:opacity-50"
                    data-testid="discount-save-btn"
                  >{saving ? "Saving..." : discountModal.discount ? "Update" : "Apply"}</button>
                </div>
                <p className="text-[10px] text-gray-600 text-center">
                  {discountModal.stripe_subscription_id ? "Will update Stripe subscription immediately" : "Saved locally (no active Stripe subscription)"}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bulk Discount Modal */}
      <AnimatePresence>
        {bulkModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setBulkModal(false)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-[#0F172A] border border-[#1E293B] rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="bulk-discount-modal">
              <div className="flex justify-between items-center mb-5">
                <div>
                  <h3 className="text-lg font-bold text-white">Bulk Discount</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Apply to {selected.size} athlete{selected.size !== 1 ? "s" : ""}</p>
                </div>
                <button onClick={() => setBulkModal(false)} className="p-1 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Discount Percentage *</label>
                  <div className="relative">
                    <input type="number" min="1" max="100" step="0.01" value={bulkForm.percent_off}
                      onChange={(e) => setBulkForm({ ...bulkForm, percent_off: e.target.value })}
                      placeholder="e.g., 20"
                      className="w-full px-3 py-2.5 bg-[#0B0F1A] border border-[#1E293B] rounded-xl text-white text-sm focus:border-blue-500 outline-none pr-10"
                      data-testid="bulk-discount-percent-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Note / Reason</label>
                  <input type="text" value={bulkForm.note} onChange={(e) => setBulkForm({ ...bulkForm, note: e.target.value })}
                    placeholder="e.g., Seasonal promo, group deal"
                    className="w-full px-3 py-2.5 bg-[#0B0F1A] border border-[#1E293B] rounded-xl text-white text-sm focus:border-blue-500 outline-none"
                    data-testid="bulk-discount-note-input"
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setBulkModal(false)} className="flex-1 px-4 py-2.5 bg-[#1E293B] text-gray-300 rounded-xl font-medium hover:bg-[#2D3B4F] transition-colors">Cancel</button>
                  <button onClick={handleBulkDiscount} disabled={bulkSaving}
                    className="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-600 to-amber-500 text-white rounded-xl font-medium hover:from-amber-500 hover:to-amber-400 transition-all disabled:opacity-50"
                    data-testid="bulk-discount-apply-btn"
                  >{bulkSaving ? "Applying..." : `Apply to ${selected.size}`}</button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Discount History Modal */}
      <AnimatePresence>
        {historyModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setHistoryModal(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
              className="bg-[#0F172A] border border-[#1E293B] rounded-2xl p-6 w-full max-w-sm max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="history-modal">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white">Discount History</h3>
                  <p className="text-xs text-gray-400">{historyModal.name || historyModal.email}</p>
                </div>
                <button onClick={() => setHistoryModal(null)} className="p-1 text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 overflow-y-auto space-y-2">
                {discountHistory.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-8">No discount history</p>
                ) : (
                  discountHistory.map((h, i) => (
                    <div key={i} className="bg-[#141B2D] border border-[#1E293B] rounded-xl p-3">
                      <div className="flex justify-between items-center">
                        <span className={`text-xs font-bold ${h.action === "removed" ? "text-red-400" : "text-amber-400"}`}>
                          {h.action === "removed" ? "Removed" : `${h.percent_off}% off`}
                        </span>
                        <span className="text-[10px] text-gray-600">
                          {h.stripe_applied ? "Stripe synced" : "Local only"}
                        </span>
                      </div>
                      {h.note && <p className="text-xs text-gray-400 mt-1">{h.note}</p>}
                      <p className="text-[10px] text-gray-600 mt-1">{new Date(h.created_at).toLocaleString()}</p>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubscribersManager;
